// Libraries
import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  JSXElementConstructor,
  ReactElement,
} from "react";
import { DocumentProps } from "@react-pdf/renderer";

// Hooks
import { useFeatures } from "../store/hooks/useFeatures";
import { useTrackExport } from "hooks/useTrackExport";
import { useSelector } from "react-redux";
import { AppState } from "store/reducers/Reducer";

// Components
import { CoverageSummaryPDF } from "components/common/pdf/Exports/CoverageSummaryPDF/CoverageSummaryPDF";
import { CoveragePDF } from "components/common/pdf/Exports/CoveragePDF/CoveragePDF";
import { PDFExport } from "components/common/pdf/PDFExport";

const pdfExports = {
  coverage: {
    title: "Coverage",
    content: CoveragePDF,
  },
  coverageSummary: {
    title: "Coverage Summary",
    content: CoverageSummaryPDF,
  },
};

type PDFExportKeys = keyof typeof pdfExports;

interface ExportContextData {
  handleOpenExport: (current: PDFExportKeys, customCoverageData?: any) => void; // eslint-disable-line
}

const defaultExportContextData: ExportContextData = {
  handleOpenExport: () => {},
};

const ExportContext = createContext<ExportContextData>(defaultExportContextData);

export const useExportContext = () => useContext(ExportContext);

export const ExportContextProvider = ({ children }: { children: ReactNode }) => {
  const { getCoverage, coverage } = useFeatures();
  const trackExport = useTrackExport();
  const project = useSelector((state: AppState) => state.project);
  const projectId = project.project_id?.toString();
  const documentId = (
    project.active_document_id ||
    project.selected_document_id ||
    project.documents?.[0]?.document_id
  )?.toString();

  const [isOpen, setIsOpen] = useState(false);
  const [exportType, setExportType] = useState<PDFExportKeys>();
  const [title, setTitle] = useState("");
  const [customCoverageData, setCustomCoverageData] = useState<any>(null);

  const handleCloseExport = () => {
    setIsOpen(false);
    setCustomCoverageData(null);
  };

  const handleOpenExport = (current: PDFExportKeys, customData?: any) => {
    setExportType(current);
    setIsOpen(true);
    setTitle(pdfExports[current].title);
    setCustomCoverageData(customData || null);
  };

  useEffect(() => {
    if (!exportType || !isOpen) return;

    // Only fetch from backend if we don't have custom data
    if (!customCoverageData) {
      const fetchFeatures = async () => {
        switch (exportType) {
          case "coverage":
            getCoverage.mutate();
            break;
          case "coverageSummary":
            getCoverage.mutate();
            break;
          default:
            break;
        }
      };
      fetchFeatures();
    }
  }, [exportType, isOpen, customCoverageData]);

  const Content = (): ReactElement<DocumentProps, string | JSXElementConstructor<any>> => {
    // Use custom data if available, otherwise use the coverage from Redux store
    const coverageData = customCoverageData || coverage;

    switch (exportType) {
      case "coverage":
        return <CoveragePDF coverage={coverageData} />;
      case "coverageSummary":
      default:
        return <CoverageSummaryPDF coverage={coverageData} />;
    }
  };

  return (
    <ExportContext.Provider value={{ handleOpenExport }}>
      {children}
      {exportType && (
        <PDFExport
          title={title}
          isOpen={isOpen}
          onClose={handleCloseExport}
          onDownload={() => {
            if (!projectId || !documentId) return;
            trackExport({
              projectId,
              documentId,
              exportType: exportType === "coverageSummary" ? "coverage_summary" : "coverage",
              format: "pdf",
            });
          }}
        >
          <Content />
        </PDFExport>
      )}
    </ExportContext.Provider>
  );
};
